import React from 'react';
import { Message, Role, Place } from '../types';
import { PlaceCard } from './PlaceCard';
import { ChatRouteSuggestions } from './ChatRouteSuggestions';

interface ChatMessageProps {
  message: Message;
  onPlaceSelect?: (placeId: string) => void;
  onRouteStopOpenDetails?: (place: Place) => void;
  canRemoveRouteStop?: (place: Place, routeIndex: number) => boolean;
  onRouteStopRemove?: (place: Place, routeIndex: number) => void;
  onOpenMapFromRouteSuggestions?: (routeIndex: number) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onPlaceSelect,
  onRouteStopOpenDetails,
  canRemoveRouteStop,
  onRouteStopRemove,
  onOpenMapFromRouteSuggestions,
}) => {
  const isUser = message.role === Role.USER;
  const isPreparing =
    message.role === Role.ASSISTANT &&
    typeof message.id === 'string' &&
    message.id.startsWith('assistant-preparing-');
  const hasRouteSuggestions =
    !isUser &&
    !isPreparing &&
    message.routeSuggestions &&
    message.routeSuggestions.length > 0;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 px-2 fade-in`}>
      <div
        className={`${
          hasRouteSuggestions ? 'max-w-[min(100%,40rem)] w-full' : 'max-w-[85%]'
        } ${
          isUser
            ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm'
            : 'bg-white text-gray-900 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100'
        } px-4 py-3`}
      >
        {/* Message text */}
        {isPreparing ? (
          <div className="flex space-x-1.5 items-center h-5">
            <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
          </div>
        ) : (
          <p className={`text-[15px] leading-relaxed ${isUser ? 'text-white' : 'text-gray-800'}`}>
            {message.text}
          </p>
        )}

        {/* Route option carousels (agent suggestions) */}
        {hasRouteSuggestions && (
          <ChatRouteSuggestions
            suggestions={message.routeSuggestions!}
            onOpenPlaceDetails={onRouteStopOpenDetails}
            canRemoveStop={canRemoveRouteStop}
            onRemoveStop={onRouteStopRemove}
            onOpenMap={onOpenMapFromRouteSuggestions}
          />
        )}

        {/* Place cards if any (non-route ranked places, etc.) */}
        {message.places && message.places.length > 0 && !hasRouteSuggestions && (
          <div className="mt-3 space-y-2">
            {message.places.map((place) => (
              <PlaceCard
                key={place.place_id}
                place={place}
                onSelect={() => onPlaceSelect?.(place.place_id)}
              />
            ))}
          </div>
        )}

        {/* Timestamp */}
        {!isPreparing && (
          <div
            className={`text-[10px] mt-2 ${
              isUser ? 'text-blue-100' : 'text-gray-400'
            }`}
          >
            {formatTime(message.timestamp)}
          </div>
        )}
      </div>
    </div>
  );
};

// Loading indicator component (dots only)
export const LoadingIndicator: React.FC = () => {
  return (
    <div className="flex justify-start mb-4 px-2">
      <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100">
        <div className="flex space-x-1.5 items-center h-5">
          <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full bounce-dot"></div>
        </div>
      </div>
    </div>
  );
};

/** Spinning circle for status bars */
const SpinningCircle: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`relative z-10 flex-shrink-0 rounded-full border-2 border-blue-600 border-t-transparent animate-spin ${className}`}
    style={{ width: 14, height: 14 }}
  />
);

/** Flashing status bar in chat: left-aligned, half size, agent side. Flash sweeps left to right. */
export const AgentStatusBar: React.FC<{ message: string }> = ({ message }) => {
  return (
    <div className="agent-status-bar-flash flex items-center gap-1 rounded-lg border border-blue-200 text-blue-800 py-1 px-2 text-xs max-w-[50%]">
      <SpinningCircle />
      <span className="relative z-10 font-medium truncate">{message}</span>
    </div>
  );
};

/** On map when chat closed: shows "Planning", blocks map interaction. */
export const MapLoadingBar: React.FC = () => {
  return (
    <div className="agent-status-bar-flash flex items-center gap-1 rounded-lg border border-blue-200 text-blue-800 py-1 px-2 text-xs max-w-[50%]">
      <SpinningCircle />
      <span className="relative z-10 font-medium">Planning</span>
    </div>
  );
};

// Helper function to format timestamp
function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
